import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import ProductCard from './ProductCard';
import { CustomLoader } from '../../common/CustomLoader';

export default function StorePage() {
  const location = useLocation();
  const category = location.state?.category; // From Categories on Home Page to filter Data

  const [lastVisible, setLastVisible] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const limitPerPage = 20;
  const [products, setProducts] = useState([]);

  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      setInitialLoading(true);
      try {
        const produtctRef = collection(db, 'products');
        let queryConstraints = [];
        queryConstraints.push(orderBy('addedDateTime', 'desc'));
        queryConstraints.push(where('isDeleted', '==', false));
        if (category) {
          queryConstraints.push(where("categoriesNames", "array-contains", category.trim()));
        }
        queryConstraints.push(limit(limitPerPage));

        const productsQuery = query(produtctRef, ...queryConstraints);

        // Fetch products
        const productsSnapshot = await getDocs(productsQuery);

        // Map snapshot to array of product data
        const productsList = productsSnapshot.docs.map(doc => ({
          ...doc.data()
        }));

        // Update state with products
        setProducts(productsList);
        setLastVisible(productsSnapshot.docs[productsSnapshot.docs.length - 1]);
        if (productsSnapshot.docs.length < limitPerPage) {
          setHasMore(false);
        }

        setInitialLoading(false);
      } catch (error) {
        console.log('Error fetching products: ', error);
        setInitialLoading(false);
      }
    };

    // Call the function to fetch products when component mounts
    fetchProducts();
  }, [category]);

  const loadMoreProducts = async () => {
    if (!lastVisible || loading) return;

    setLoading(true);
    try {
      const produtctRef = collection(db, 'products');
      let queryConstraints = [];
      queryConstraints.push(orderBy('addedDateTime', 'desc'));
      queryConstraints.push(where('isDeleted', '==', false));

      if (searchInput.length > 0) {
        queryConstraints.push(where('keywords', 'array-contains', searchInput.toLocaleLowerCase()));
      }
      else {
        if (category) {
          queryConstraints.push(where("categoriesNames", "array-contains", category.trim()));
        }
      }

      queryConstraints.push(startAfter(lastVisible));
      queryConstraints.push(limit(limitPerPage));
      const productsQuery = query(produtctRef, ...queryConstraints);
      // Fetch products
      const productsSnapshot = await getDocs(productsQuery);
      // Map snapshot to array of product data
      const productsList = productsSnapshot.docs.map(doc => ({
        ...doc.data()
      }));

      // Update state with products
      setProducts(prev => [...prev, ...productsList]);
      setLastVisible(productsSnapshot.docs[productsSnapshot.docs.length - 1]);
      if (productsSnapshot.docs.length < limitPerPage) {
        setHasMore(false);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching Products: ", error);
      setLoading(false);
    }
  };


  const performSearch = async () => {
    setInitialLoading(true);
    try {
      const produtctRef = collection(db, 'products');
      let queryConstraints = [];
      queryConstraints.push(orderBy('addedDateTime', 'desc'));
      queryConstraints.push(where('isDeleted', '==', false));
      if (searchInput.length > 0) {
        queryConstraints.push(where("keywords", "array-contains", searchInput.toLocaleLowerCase()));
      }
      queryConstraints.push(limit(limitPerPage));

      const productsQuery = query(produtctRef, ...queryConstraints);

      // Fetch products
      const productsSnapshot = await getDocs(productsQuery);

      // Map snapshot to array of product data
      const productsList = productsSnapshot.docs.map(doc => ({
        ...doc.data()
      }));

      // Update state with products
      setProducts(productsList);
      setLastVisible(productsSnapshot.docs[productsSnapshot.docs.length - 1]);
      if (productsSnapshot.docs.length < limitPerPage) {
        setHasMore(false);
      }
      else {
        setHasMore(true);
      }

      setInitialLoading(false);
    } catch (error) {
      console.log('Error fetching products: ', error);
      setInitialLoading(false);
    }
  };

  return (
    <div className="p-4 min-h-[70vh]">

      <div className="flex flex-col md:flex-row lg:flex-row items-center justify-between gap-4 w-full mt-1 mb-4">
        <div className="flex flex-wrap justify-center gap-4">
        </div>

        <div className="relative w-full max-w-md">
          <input
            className="w-full h-12 px-4 pr-16 rounded-lg font-medium 
            bg-input-bg border border-input-border placeholder-input-placeholder 
            text-sm focus:outline-none focus:border-input-focus-border focus:bg-input-focus-bg"
            type="text"
            value={searchInput}
            placeholder="Search by Product Title"
            onKeyDown={(e) => e.key === "Enter" && performSearch()}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button
            className="absolute inset-y-0 right-0 px-4 text-sm font-semibold 
            bg-light-green text-light-text-color rounded-r-lg 
            hover:bg-bright-green hover:text-white 
            transition duration-300 flex items-center"
            onClick={performSearch}
          >
            Search
          </button>
        </div>
      </div>


      {initialLoading ? (
        renderShimmerEffect() // Shimmer loading effect
      ) : products.length === 0 ? (
        <div className="flex justify-center mt-4"><p>No Products Found.</p></div>
      ) : (
        <section className="mb-8 bg-main-bg">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4 text-dark-text-color">🔥 Latest in Collection 🔥</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {products.map((product) => (
                <ProductCard product={product} key={product.id} />
              ))}
            </div>
          </div>
        </section>
      )}

      {loading ? (
        <div className="flex justify-center mt-4">
          <CustomLoader />
        </div>
      ) : (
        hasMore && (
          <div className="flex justify-center mt-4">
            <button
              onClick={loadMoreProducts}
              className="bg-bright-green text-white font-semibold px-6 py-3 rounded-full shadow-md hover:bg-white hover:text-bright-green transition duration-300"
            >
              Load More
            </button>
          </div>
        )
      )}

      {!hasMore && !loading && (
        <div className="flex justify-center mt-4">
          <p className="text-light-text-color">No more Products available</p>
        </div>
      )}
    </div>
  );
}


const renderShimmerEffect = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8">
    {Array.from({ length: 4 }).map((_, index) => (
      <div
        key={index}
        className="bg-white rounded-2xl shadow-md py-6 flex flex-col items-center justify-center animate-pulse"
      >
        <div className="w-full h-40 bg-gray-200 rounded-md mb-4"></div>
        <div className="h-6 bg-gray-200 w-3/4 mb-2 rounded-md"></div>
        <div className="flex items-center justify-center gap-1 mb-4">
          <div className="h-4 bg-gray-200 w-16 rounded-md"></div>
          <div className="h-4 bg-gray-200 w-16 rounded-md"></div>
        </div>
        <div className="text-center flex justify-center items-center gap-4">
          <div className="h-6 bg-gray-200 w-20 rounded-md"></div>
          <div className="h-10 bg-gray-200 w-28 rounded-md"></div>
        </div>
      </div>
    ))}
  </div>
);
